// src/screens/sales/executor/hooks/useExecutor.ts

import { useCallback, useEffect, useState } from 'react'
import { ExecutorInterface } from '../../../../common/types/interfaces'
import { getSalesApi } from '../../../../salesApi/salesApi'
import { selectCurrentCluster } from '../../../../store/slices/currentClusterSlice'
import { useSelector } from 'react-redux'
import { useSearchWithDebounce } from '../../../../common/hooks/useSearchWithDebounce'
import { useQr } from '../../../../common/components/qr/hooks/useQr'

const initialValue: ExecutorInterface = {
  name: '',
  phone_number: '',
  position: '',
  isDriver: false,
  canServe: false,
  trackLocation: false,
  isActive: true,
  deleted: false,
  motivationId: 0,
  storeIds: [],
  publish: false,
  publishMotivation: false,
}

export const useExecutor = () => {
  const [modalActive, setModalActive] = useState<boolean>(false)
  const [detailsModalActive, setDetailsModalActive] = useState<boolean>(false)
  const [edit, setEdit] = useState<boolean>(false)
  const [salesApi] = useState(getSalesApi())
  const [data, setData] = useState<ExecutorInterface>(initialValue)
  const [list, setList] = useState<ExecutorInterface[]>([])
  const [current, setCurrent] = useState<ExecutorInterface>()
  const currentCluster = useSelector(selectCurrentCluster)
  const [loading, setLoading] = useState(true)
  const [qrExecutorId, setQrExecutorId] = useState<number | null>(null)

    const { searchQuery, handleSearch, clearSearch, filteredItems } = useSearchWithDebounce({
    items: list,
    searchFields: ['id', 'name', 'phone_number', 'position'],
  })


  const getList = useCallback(async () => {
    try {
      const result = await salesApi.getExecutorList({ sort: 'name', order: 'ASC' })
      if (result) setList(result.data)
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }, [salesApi])

  const attachUser = useCallback(
    async (executorId: number, qrCode: string) => {
      try {
        await salesApi.attachUserToExecutor({ executorId, qrCode })
        await getList()
        setQrModalActive(false)
      } catch (error) {
        setQrModalActive(false)
      }
    },
    [salesApi, getList],
  )

  const handleQrScan = useCallback(
    (qrCode: string) => {
      if (qrExecutorId) {
        attachUser(qrExecutorId, qrCode)
      }
      setQrExecutorId(null)
    },
    [qrExecutorId, attachUser],
  )

  const {
    modalActive: qrModalActive,
    setModalActive: setQrModalActive,
    ref: qrRef,
    startScan,
  } = useQr(handleQrScan)

  const startCreate = () => {
    setCurrent(undefined)
    setData(initialValue)
    setEdit(false)
    setModalActive(true)
  }

  const startEdit = (el: ExecutorInterface) => {
    const readyEl = {
      ...el,
      storeIds: el.stores?.map((s) => s.store?.id || s.storeId) || [],
    }
    setCurrent(readyEl)
    setData(readyEl)
    setEdit(true)
    setModalActive(true)
  }

  const startDuplicate = useCallback((sourceItem: ExecutorInterface) => {
    const { id, createdAt, updatedAt, uuid, clusterId, deleted, ...rest } = sourceItem

    const duplicatedItem: ExecutorInterface = {
      name: `${sourceItem.name} (копия)`,
      phone_number: sourceItem.phone_number,
      position: sourceItem.position,
      isDriver: sourceItem.isDriver,
      canServe: sourceItem.canServe,
      trackLocation: sourceItem.trackLocation,
      isActive: sourceItem.isActive,
      deleted: false,
      clusterId: sourceItem.clusterId,
      motivationId: sourceItem.motivationId,
      storeIds: sourceItem.stores?.map((s) => s.store?.id || s.storeId) || [],
      publish: sourceItem.publish,
      publishMotivation: sourceItem.publishMotivation,
    }

    setCurrent(duplicatedItem)
    setEdit(false)
    setData(duplicatedItem)
    setModalActive(true)
  }, [])

  const showDetails = useCallback((el: ExecutorInterface) => {
    setCurrent(el)
    setDetailsModalActive(true)
  }, [])

  const handleAttachUser = useCallback(
    (executorId: number) => {
      setQrExecutorId(executorId)
      startScan()
    },
    [startScan],
  )


  const toggleActive = useCallback(async (id: number, isActive: boolean) => {
  try {
    await salesApi.updateExecutor({ id, isActive })
    await getList()
  } catch (error) {
    console.error('Toggle active error:', error)
  }
}, [salesApi, getList])

const togglePublish = useCallback(async (id: number, publish: boolean) => {
  try {
    await salesApi.updateExecutor({ id, publish })
    await getList()
  } catch (error) {
    console.error('Toggle publish error:', error)
  }
}, [salesApi, getList])

const togglePublishMotivation = useCallback(async (id: number, publishMotivation: boolean) => {
  try {
    await salesApi.updateExecutor({ id, publishMotivation })
    await getList()
  } catch (error) {
    console.error('Toggle publishMotivation error:', error)
  }
}, [salesApi, getList])


  useEffect(() => {
    if (currentCluster) getList()
  }, [currentCluster, getList])

  useEffect(() => {
    if (!modalActive) {
      setData(initialValue)
      setEdit(false)
    }
  }, [modalActive])

  return {
    getList,
    modalActive,
    setModalActive,
    startCreate,
    startEdit,
    startDuplicate,
    edit,
    data,
    setData,
    list: filteredItems,
    detailsModalActive,
    setDetailsModalActive,
    showDetails,
    current,
    loading,
    searchQuery,
    handleSearch,
    clearSearch,
    qrModalActive,
    setQrModalActive,
    qrRef,
    handleAttachUser,
    handleQrScan,
    togglePublishMotivation, toggleActive, togglePublish
  }
}
